import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ToastProvider, useToast } from "../components/Toast";
import { useConfirm } from "../components/ConfirmDialog";
import AdminSidebar from "../components/AdminSidebar";
import { fetchBlogsAdmin, deleteBlog, updateBlog } from "../../services/api";

const SERVER_URL = process.env.REACT_APP_SERVER_URL || "";

const styles = `
  .bpage-layout {
    display: grid;
    grid-template-columns: 220px minmax(0, 1fr);
    min-height: 100vh;
    padding-top: 64px;
    background: #f0f2f8;
  }
  @media (max-width: 768px) { .bpage-layout { grid-template-columns: 1fr; } }
  .bpage-content { padding: 36px 32px; min-width: 0; margin-top: 20px; }
  @media (max-width: 900px) { .bpage-content { padding: 24px 14px; } }
  .bpage-header {
    display: flex; align-items: center; justify-content: space-between;
    flex-wrap: wrap; gap: 14px; margin-bottom: 26px;
  }
  .bpage-title {
    font-family: "Syne", sans-serif;
    font-size: clamp(18px, 3vw, 24px);
    font-weight: 800; color: #1a1a2e;
  }
  .bpage-add-btn {
    background: #6246ea; color: #fff; border: none; border-radius: 9px;
    padding: 10px 18px; font-size: 14px; font-weight: 600; cursor: pointer;
  }
  .bpage-row {
    display: flex; gap: 16px; align-items: center;
    background: #fff; border: 1px solid #e8eaf2; border-radius: 14px;
    padding: 14px 18px; margin-bottom: 12px;
  }
  .bpage-thumb {
    width: 84px; height: 56px; object-fit: cover; border-radius: 8px;
    background: linear-gradient(135deg,#6246ea22,#10b98122);
    flex-shrink: 0; display: flex; align-items: center; justify-content: center;
    font-size: 22px;
  }
  .bpage-info { flex: 1; min-width: 0; }
  .bpage-blog-title {
    font-weight: 700; font-size: 15px; color: #1a1a2e;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .bpage-slug { font-size: 12.5px; color: #6246ea; margin-top: 2px; }
  .bpage-meta { font-size: 12px; color: #9ca3af; margin-top: 3px; }
  .bpage-badge {
    font-size: 11px; font-weight: 700; padding: 4px 10px;
    border-radius: 20px; flex-shrink: 0;
  }
  .bpage-badge.published { background: #10b98118; color: #059669; }
  .bpage-badge.draft { background: #f59e0b18; color: #b45309; }
  .bpage-actions { display: flex; gap: 8px; flex-shrink: 0; flex-wrap: wrap; }
  .bpage-btn {
    border: 1px solid #d9dbe8; background: #fff; border-radius: 8px;
    padding: 7px 12px; font-size: 12.5px; font-weight: 600; cursor: pointer;
  }
  .bpage-btn.danger { color: #ef4444; border-color: #ef444455; }
  .bpage-empty { text-align: center; padding: 70px 20px; color: #9ca3af; }
  @media (max-width: 700px) {
    .bpage-row { flex-wrap: wrap; }
  }
`;

const BlogsAdminInner = () => {
  const toast = useToast();
  const { confirm, ConfirmComponent } = useConfirm();
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    fetchBlogsAdmin()
      .then((res) => setBlogs(res.data.data || []))
      .catch(() => toast("Failed to load blogs", "error"))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (blog) => {
    const ok = await confirm({
      title: "Delete blog?",
      message: `"${blog.title}" will be permanently deleted.`,
    });
    if (!ok) return;
    try {
      await deleteBlog(blog.id);
      toast("Blog deleted", "success");
      load();
    } catch (e) {
      toast(e.response?.data?.message || "Failed to delete blog", "error");
    }
  };

  const toggleStatus = async (blog) => {
    const next = blog.status === "published" ? "draft" : "published";
    try {
      await updateBlog(blog.id, { status: next });
      toast(next === "published" ? "Blog published" : "Moved to draft", "success");
      load();
    } catch (e) {
      toast(e.response?.data?.message || "Failed to update status", "error");
    }
  };

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  return (
    <div className="bpage-layout">
      <style>{styles}</style>
      {ConfirmComponent}
      <AdminSidebar
        section="blogs"
        onSectionChange={(id) => navigate("/admin", { state: { section: id } })}
      />
      <div className="bpage-content">
        <div className="bpage-header">
          <div className="bpage-title">📝 Blogs ({blogs.length})</div>
          <button
            className="bpage-add-btn"
            onClick={() => navigate("/admin/add-blog")}
          >
            + New Blog Post
          </button>
        </div>

        {loading ? (
          <div className="bpage-empty">Loading…</div>
        ) : blogs.length === 0 ? (
          <div className="bpage-empty">
            No blog posts yet. Click “New Blog Post” to write the first one.
          </div>
        ) : (
          blogs.map((b) => (
            <div className="bpage-row" key={b.id}>
              {b.headImage ? (
                <img
                  className="bpage-thumb"
                  src={`${SERVER_URL}${b.headImage}`}
                  alt=""
                />
              ) : (
                <div className="bpage-thumb">📝</div>
              )}
              <div className="bpage-info">
                <div className="bpage-blog-title">{b.title}</div>
                <div className="bpage-slug">/blog/{b.slug}</div>
                <div className="bpage-meta">
                  {b.category} · {fmtDate(b.createdAt)}
                </div>
              </div>
              <span className={`bpage-badge ${b.status}`}>
                {b.status === "published" ? "Published" : "Draft"}
              </span>
              <div className="bpage-actions">
                <button
                  className="bpage-btn"
                  onClick={() => window.open(`/blog/${b.slug}`, "_blank")}
                >
                  View
                </button>
                <button className="bpage-btn" onClick={() => toggleStatus(b)}>
                  {b.status === "published" ? "Unpublish" : "Publish"}
                </button>
                <button
                  className="bpage-btn"
                  onClick={() => navigate(`/admin/edit-blog/${b.id}`)}
                >
                  Edit
                </button>
                <button
                  className="bpage-btn danger"
                  onClick={() => handleDelete(b)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const BlogsAdminPage = () => (
  <ToastProvider>
    <BlogsAdminInner />
  </ToastProvider>
);

export default BlogsAdminPage;
